/**
 * CareflowPage — V2 page orchestrator for standalone Careflow settings.
 *
 * Manages the nested careflow hierarchy (Careflow → Workflow → Escalation → Output)
 * using the `getcareflows` stored procedure with transaction types:
 *   careflow_v2_add/edit/delete, workflow_v2_add/edit/delete, escalation_v2_add/edit/delete
 *
 * Architecture: Page orchestrator pattern — all state, data fetching, and
 * submit handlers live here. Child components receive data + callbacks as props.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import {
    getSession,
    getCampusId,
    getUserId,
    getSessionId,
    getCampusList,
    updateSessionCampus,
} from '../../../services/session.js';
import {
    fetchCareflows,
    fetchAreaGroups,
    fetchResidentGroups,
    fetchUserGroups,
    fetchUsers,
    fetchDevices,
    fetchEventCategories,
    fetchEventTypes,
    fetchEmergencyContacts,
    addCareflow,
    editCareflow,
    deleteCareflow,
    addWorkflow,
    editWorkflow,
    deleteWorkflow,
    addEscalation,
    editEscalation,
    deleteEscalation,
} from './api/careflowApi.js';

import CareflowCard from './components/CareflowCard.jsx';
import CareflowDrawer from './components/CareflowDrawer.jsx';
import WorkflowDrawer from './components/WorkflowDrawer.jsx';
import EscalationDrawer from './components/EscalationDrawer.jsx';
import DeleteDialog from './components/DeleteDialog.jsx';

import './careflow-page.css';

// ---------------------------------------------------------------------------
// Tab info banners
// ---------------------------------------------------------------------------
const TAB_INFO = {
    default: { strong: 'Default Careflow', text: 'serves as the final default level of priority when no higher-priority careflow takes precedence.' },
    area: { strong: 'Area Careflow', text: 'applies to events associated with areas in any attached area group when no higher-priority careflow takes precedence. This supersedes all default careflows.' },
    resident: { strong: 'Resident Careflow', text: 'applies to events associated with residents in any attached resident group when no higher-priority careflow takes precedence. This supersedes area and default careflows.' },
    device: { strong: 'Device Careflow', text: 'applies to events from specific devices when no higher-priority careflow takes precedence. This supersedes resident, area, and default careflows.' },
    event: { strong: 'Event Careflow', text: 'applies to events matching a specific event category and type. This is the highest priority careflow and supersedes all other careflows.' },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const CareflowPage = () => {
    // ── Session ──────────────────────────────────────────────────────────────
    const session = useMemo(() => getSession(), []);
    const [campusId, setCampusId] = useState(() => getCampusId());
    const campusList = useMemo(() => getCampusList() || [], []);
    const campusName = useMemo(() => {
        const match = campusList.find((c) => c.campus_id === campusId);
        return match?.campus_name || `Campus ${campusId}`;
    }, [campusId, campusList]);

    const campusSettings = useMemo(
        () => session?.campusData?.[campusId?.toString()] || {},
        [session, campusId],
    );

    const escMax = campusSettings.alert_ttl || 3600;

    // ── Feature flags for conditional tabs ────────────────────────────────────
    const useAreaCareflow = !!campusSettings.use_area_careflow;
    const useResidentCareflow = !!campusSettings.use_resident_careflow;
    const useDeviceCareflow = !!campusSettings.use_device_careflow;
    const useEventCareflow = !!campusSettings.use_event_careflow;

    // ── Tabs ─────────────────────────────────────────────────────────────────
    const TABS = useMemo(() => {
        const tabs = [{ key: 'default', label: 'Default Careflow' }];
        if (useAreaCareflow) tabs.push({ key: 'area', label: 'Area Careflow' });
        if (useResidentCareflow) tabs.push({ key: 'resident', label: 'Resident Careflow' });
        if (useDeviceCareflow) tabs.push({ key: 'device', label: 'Device Careflow' });
        if (useEventCareflow) tabs.push({ key: 'event', label: 'Event Careflow' });
        return tabs;
    }, [useAreaCareflow, useResidentCareflow, useDeviceCareflow, useEventCareflow]);

    const [activeTab, setActiveTab] = useState('default');

    // ── Data ─────────────────────────────────────────────────────────────────
    const [originData, setOriginData] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [areaGroups, setAreaGroups] = useState([]);
    const [residentGroups, setResidentGroups] = useState([]);
    const [userGroups, setUserGroups] = useState([]);
    const [users, setUsers] = useState([]);
    const [devices, setDevices] = useState([]);
    const [eventCats, setEventCats] = useState([]);
    const [eventTypes_, setEventTypes_] = useState([]);
    const [emergencyContacts, setEmergencyContacts] = useState([]);

    // ── Filtered data per tab ────────────────────────────────────────────────
    const TYPE_MAP = { default: 1, device: 3, resident: 4, area: 5, event: 6 };

    const tabData = useMemo(() => {
        const typeId = TYPE_MAP[activeTab];
        return originData.filter((c) => c.careflow_type_id === typeId);
    }, [originData, activeTab]);

    // ── Drawer / dialog state ────────────────────────────────────────────────
    const [saving, setSaving] = useState(false);
    const [expandedEscalations, setExpandedEscalations] = useState({});

    // Careflow drawer
    const [cfDrawerOpen, setCfDrawerOpen] = useState(false);
    const [cfEditTarget, setCfEditTarget] = useState(null);
    const [cfEditIndex, setCfEditIndex] = useState(null);

    // Workflow drawer
    const [wfDrawerOpen, setWfDrawerOpen] = useState(false);
    const [wfEditTarget, setWfEditTarget] = useState(null);
    const [wfParentIndex, setWfParentIndex] = useState(null);
    const [wfEditIndex, setWfEditIndex] = useState(null);

    // Escalation drawer
    const [escDrawerOpen, setEscDrawerOpen] = useState(false);
    const [escEditTarget, setEscEditTarget] = useState(null);
    const [escParentCareflowIdx, setEscParentCareflowIdx] = useState(null);
    const [escParentWorkflowIdx, setEscParentWorkflowIdx] = useState(null);
    const [escEditIndex, setEscEditIndex] = useState(null);

    // Delete dialog
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteInfo, setDeleteInfo] = useState({ title: '', message: '', action: null });

    // Toast
    const [toast, setToast] = useState(null);
    const toastTimerRef = useRef(null);
    const showToast = useCallback((type, message) => {
        clearTimeout(toastTimerRef.current);
        setToast({ type, message });
        toastTimerRef.current = setTimeout(() => setToast(null), 4000);
    }, []);
    useEffect(() => () => clearTimeout(toastTimerRef.current), []);

    // ── Data fetching ────────────────────────────────────────────────────────
    const loadData = useCallback(async () => {
        setLoaded(false);
        const [cfData, ag, rg, ug, u, dev, ec, et, contacts] = await Promise.all([
            fetchCareflows(campusId),
            fetchAreaGroups(campusId),
            fetchResidentGroups(campusId),
            fetchUserGroups(campusId),
            fetchUsers(campusId),
            fetchDevices(campusId),
            fetchEventCategories(campusId),
            fetchEventTypes(campusId),
            fetchEmergencyContacts(campusId),
        ]);
        setOriginData(cfData || []);
        setAreaGroups(ag || []);
        setResidentGroups(rg || []);
        setUserGroups(ug || []);
        setUsers(u || []);
        setDevices(dev || []);
        setEventCats(ec || []);
        setEventTypes_(et || []);
        setEmergencyContacts(contacts || []);
        setLoaded(true);
    }, [campusId]);

    useEffect(() => { loadData(); }, [loadData]);

    // ── Campus change ────────────────────────────────────────────────────────
    const handleCampusChange = (newId) => {
        // If careflow_v2 flag differs, redirect to wellness settings
        const oldData = session?.campusData?.[campusId?.toString()] || {};
        const newData = session?.campusData?.[newId?.toString()] || {};
        if (oldData.careflow_v2 !== newData.careflow_v2) {
            window.location.href = '/settings/wellness';
            return;
        }
        if (oldData.is_cloud !== newData.is_cloud) {
            window.location.reload();
            return;
        }
        updateSessionCampus(newId);
        setCampusId(newId);
        setActiveTab('default');
    };

    // ── Transaction context ──────────────────────────────────────────────────
    const txCtx = useMemo(() => ({
        campusId,
        userId: getUserId(),
        clientId: getSessionId(),
    }), [campusId]);

    // ── Escalation toggle ────────────────────────────────────────────────────
    const toggleEscalation = (workflowId, escalationId) => {
        const key = `${workflowId}-${escalationId}`;
        setExpandedEscalations((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    // ── Careflow CRUD ────────────────────────────────────────────────────────
    const openAddCareflow = () => {
        setCfEditTarget(null);
        setCfEditIndex(null);
        setCfDrawerOpen(true);
    };

    const openEditCareflow = (careflow, cIndex) => {
        setCfEditTarget(careflow);
        setCfEditIndex(cIndex);
        setCfDrawerOpen(true);
    };

    const handleSaveCareflow = async (formData) => {
        setSaving(true);
        try {
            const isEdit = !!cfEditTarget;
            if (isEdit) {
                const payload = [{
                    careflow_id: cfEditTarget.careflow_id,
                    careflow_type_id: formData.careflow_type_id,
                    event_category_id: formData.event_category_id,
                    event_type_id: formData.event_type_id,
                    careflow_name: formData.careflow_name,
                    careflow_description: formData.careflow_description,
                    window_frequency_id: formData.window_frequency_id,
                    campus_id: campusId,
                    attached_entities: formData.attached_entities,
                }];
                const oldPayload = [{
                    careflow_id: cfEditTarget.careflow_id,
                    careflow_name: cfEditTarget.careflow_name,
                    careflow_description: cfEditTarget.careflow_description,
                    attached_entities: (cfEditTarget.entities || []).map((e) => String(e.reference_id)),
                    careflow_type_id: cfEditTarget.careflow_type_id,
                    event_category_id: cfEditTarget.event_category_id,
                    event_type_id: cfEditTarget.event_type_id,
                    window_frequency_id: cfEditTarget.window_frequency_id,
                }];
                const [ok] = await editCareflow(payload, oldPayload, txCtx);
                if (ok) { showToast('success', 'Careflow updated.'); setCfDrawerOpen(false); await loadData(); }
                else { showToast('error', 'Update failed.'); }
            } else {
                const payload = [{
                    careflow_name: formData.careflow_name,
                    careflow_description: formData.careflow_description,
                    careflow_type_id: TYPE_MAP[activeTab],
                    attached_entities: formData.attached_entities,
                    event_category_id: formData.event_category_id,
                    event_type_id: formData.event_type_id,
                    window_frequency_id: 1,
                    campus_id: campusId,
                }];
                const [ok] = await addCareflow(payload, txCtx);
                if (ok) { showToast('success', 'Careflow added.'); setCfDrawerOpen(false); await loadData(); }
                else { showToast('error', 'Add failed.'); }
            }
        } finally { setSaving(false); }
    };

    /** Build cascade IDs for a careflow delete */
    const buildCareflowDeletePayload = (careflow) => {
        const workflows = careflow.workflows || [];
        const flowIds = workflows.map((w) => w.workflow_id);
        const escIds = [];
        const outputIds = [];
        for (const w of workflows) {
            for (const e of (w.escalations || [])) {
                escIds.push(e.escalation_id);
                for (const o of (e.outputs || [])) outputIds.push(o.output_id);
            }
        }
        return [{ careflow_id: careflow.careflow_id, campus_id: campusId, workflows: flowIds, escalations: escIds, outputs: outputIds }];
    };

    /** Direct delete (from inline drawer confirmation) */
    const handleDeleteCareflow = async (careflow) => {
        setSaving(true);
        try {
            const [ok] = await deleteCareflow(buildCareflowDeletePayload(careflow), txCtx);
            if (ok) { showToast('success', 'Careflow removed.'); setCfDrawerOpen(false); await loadData(); }
            else { showToast('error', 'Removal failed.'); }
        } finally { setSaving(false); }
    };

    /** Dialog-based delete (from card-level delete buttons) */
    const confirmDeleteCareflow = (careflow, cIndex) => {
        setDeleteInfo({
            title: 'Delete Careflow',
            message: `Are you sure you want to delete "${careflow.careflow_name}"? This will also remove all workflows, escalations, and outputs.`,
            action: async () => {
                setSaving(true);
                try {
                    const [ok] = await deleteCareflow(buildCareflowDeletePayload(careflow), txCtx);
                    if (ok) { showToast('success', 'Careflow removed.'); setDeleteOpen(false); await loadData(); }
                    else { showToast('error', 'Removal failed.'); }
                } finally { setSaving(false); }
            },
        });
        setDeleteOpen(true);
    };

    // ── Workflow CRUD ────────────────────────────────────────────────────────
    const openAddWorkflow = (careflow, cIndex) => {
        setWfEditTarget(null);
        setWfParentIndex(cIndex);
        setWfEditIndex(null);
        setCfEditTarget(careflow);
        setWfDrawerOpen(true);
    };

    const openEditWorkflow = (careflow, cIndex, workflow, wIndex) => {
        setCfEditTarget(careflow);
        setWfEditTarget(workflow);
        setWfParentIndex(cIndex);
        setWfEditIndex(wIndex);
        setWfDrawerOpen(true);
    };

    const handleSaveWorkflow = async (formData) => {
        setSaving(true);
        try {
            const isEdit = !!wfEditTarget;
            const careflowId = cfEditTarget?.careflow_id;

            if (isEdit) {
                const payload = [{
                    workflow_id: wfEditTarget.workflow_id,
                    workflow_name: formData.workflow_name,
                    window_start: formData.window_start,
                    window_end: formData.window_end,
                    campus_id: campusId,
                    is_mute: formData.is_mute,
                }];
                const oldPayload = [{
                    workflow_id: wfEditTarget.workflow_id,
                    workflow_name: wfEditTarget.workflow_name,
                    window_start: wfEditTarget.window_start,
                    window_end: wfEditTarget.window_end,
                    is_mute: wfEditTarget.is_mute,
                }];
                const [ok] = await editWorkflow(payload, oldPayload, txCtx);
                if (ok) { showToast('success', 'Workflow updated.'); setWfDrawerOpen(false); await loadData(); }
                else { showToast('error', 'Update failed.'); }
            } else {
                const payload = [{
                    workflow_name: formData.workflow_name,
                    is_mute: formData.is_mute,
                    window_start: formData.window_start,
                    window_end: formData.window_end,
                    campus_id: campusId,
                    careflow_id: careflowId,
                }];
                const [ok] = await addWorkflow(payload, txCtx);
                if (ok) { showToast('success', 'Workflow added.'); setWfDrawerOpen(false); await loadData(); }
                else { showToast('error', 'Add failed.'); }
            }
        } finally { setSaving(false); }
    };

    /** Build cascade IDs for a workflow delete */
    const buildWorkflowDeletePayload = (workflow) => {
        const escalations = workflow.escalations || [];
        const escIds = escalations.map((e) => e.escalation_id);
        const outputIds = [];
        for (const e of escalations) {
            for (const o of (e.outputs || [])) outputIds.push(o.output_id);
        }
        return [{ workflow_id: workflow.workflow_id, campus_id: campusId, escalations: escIds, outputs: outputIds }];
    };

    /** Direct delete (from inline drawer confirmation) */
    const handleDeleteWorkflow = async (workflow) => {
        setSaving(true);
        try {
            const [ok] = await deleteWorkflow(buildWorkflowDeletePayload(workflow), txCtx);
            if (ok) { showToast('success', 'Workflow removed.'); setWfDrawerOpen(false); await loadData(); }
            else { showToast('error', 'Removal failed.'); }
        } finally { setSaving(false); }
    };

    /** Dialog-based delete (from card-level delete buttons) */
    const confirmDeleteWorkflow = (careflow, cIndex, workflow, wIndex) => {
        setDeleteInfo({
            title: 'Delete Workflow',
            message: `Are you sure you want to delete "${workflow.workflow_name}"? This will also remove all escalations and outputs.`,
            action: async () => {
                setSaving(true);
                try {
                    const [ok] = await deleteWorkflow(buildWorkflowDeletePayload(workflow), txCtx);
                    if (ok) { showToast('success', 'Workflow removed.'); setDeleteOpen(false); await loadData(); }
                    else { showToast('error', 'Removal failed.'); }
                } finally { setSaving(false); }
            },
        });
        setDeleteOpen(true);
    };

    // ── Escalation CRUD ──────────────────────────────────────────────────────
    const openAddEscalation = (careflow, cIndex, workflow, wIndex) => {
        setCfEditTarget(careflow);
        setEscEditTarget(null);
        setEscParentCareflowIdx(cIndex);
        setEscParentWorkflowIdx(wIndex);
        setEscEditIndex(null);
        setWfEditTarget(workflow);
        setEscDrawerOpen(true);
    };

    const openEditEscalation = (careflow, cIndex, workflow, wIndex, escalation, eIndex) => {
        setCfEditTarget(careflow);
        setWfEditTarget(workflow);
        setEscEditTarget(escalation);
        setEscParentCareflowIdx(cIndex);
        setEscParentWorkflowIdx(wIndex);
        setEscEditIndex(eIndex);
        setEscDrawerOpen(true);
    };

    const handleSaveEscalation = async (formData) => {
        setSaving(true);
        try {
            const isEdit = !!escEditTarget;
            const workflowId = wfEditTarget?.workflow_id;

            if (isEdit) {
                const payload = [{
                    escalation_id: escEditTarget.escalation_id,
                    period_start: formData.period_start,
                    period_end: formData.period_end,
                    campus_id: campusId,
                    escalation_name: formData.escalation_name,
                    outputs: formData.outputs,
                }];
                const oldPayload = [{
                    escalation_name: escEditTarget.escalation_name,
                    period_start: escEditTarget.period_start,
                    period_end: escEditTarget.period_end,
                }];
                const [ok] = await editEscalation(payload, oldPayload, txCtx);
                if (ok) { showToast('success', 'Escalation updated.'); setEscDrawerOpen(false); await loadData(); }
                else { showToast('error', 'Update failed.'); }
            } else {
                const payload = [{
                    workflow_id: workflowId,
                    period_start: formData.period_start,
                    period_end: formData.period_end,
                    campus_id: campusId,
                    escalation_name: formData.escalation_name,
                    outputs: formData.outputs,
                }];
                const [ok] = await addEscalation(payload, txCtx);
                if (ok) { showToast('success', 'Escalation added.'); setEscDrawerOpen(false); await loadData(); }
                else { showToast('error', 'Add failed.'); }
            }
        } finally { setSaving(false); }
    };

    const handleDeleteEscalation = async (escalation) => {
        const outputs = escalation.outputs || [];
        const outIds = outputs.map((o) => o.output_id);

        setSaving(true);
        try {
            const payload = [{ escalation_id: escalation.escalation_id, campus_id: campusId, outputs: outIds }];
            const [ok] = await deleteEscalation(payload, txCtx);
            if (ok) { showToast('success', 'Escalation removed.'); setEscDrawerOpen(false); await loadData(); }
            else { showToast('error', 'Removal failed.'); }
        } finally { setSaving(false); }
    };

    // ── Active tab type ID ───────────────────────────────────────────────────
    const activeTypeId = TYPE_MAP[activeTab];
    const info = TAB_INFO[activeTab];

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="ss-page">
                    {/* Toast notification */}
                    {toast ? (
                        <div
                            className={`ap-toast ${toast.type === 'success' ? 'ap-toast--success' : 'ap-toast--error'}`}
                            role="status"
                            aria-live="polite"
                        >
                            {toast.type === 'success' ? (
                                <svg className="ap-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            ) : (
                                <svg className="ap-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                                </svg>
                            )}
                            <span>{toast.message}</span>
                            <button
                                type="button"
                                onClick={() => { clearTimeout(toastTimerRef.current); setToast(null); }}
                                className="ap-toast__close"
                                aria-label="Dismiss"
                            >
                                <svg className="ap-icon-xs" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    ) : null}

                    {/* Header */}
                    <div className="dp-page-header">
                        <div className="dp-page-header__inner">
                            <div className="dp-breadcrumb-row">
                                <nav className="dp-breadcrumb">
                                    <span className="dp-breadcrumb__text">Settings</span>
                                    <span className="dp-breadcrumb__separator">/</span>
                                    <span className="dp-breadcrumb__current">Careflow</span>
                                </nav>
                                <div className="dp-campus-row">
                                    {/* New Careflow button (not for Default tab when one already exists) */}
                                    {(activeTab !== 'default' || tabData.length === 0) && loaded ? (
                                        <button className="dp-btn dp-btn--primary dp-btn--sm" onClick={openAddCareflow}>
                                            <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true" style={{ marginRight: '0.25rem' }}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                            </svg>
                                            New Careflow
                                        </button>
                                    ) : null}

                                    {/* Campus selector */}
                                    <label htmlFor="cf-campus-selector" className="dp-campus-label">Campus:</label>
                                    {campusList.length > 1 ? (
                                        <select
                                            id="cf-campus-selector"
                                            value={campusId}
                                            onChange={(e) => handleCampusChange(parseInt(e.target.value, 10))}
                                            className="dp-campus-select"
                                        >
                                            {campusList.map((c) => (
                                                <option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="dp-campus-name">{campusName}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tab bar */}
                    <div className="pp-tab-bar">
                        {TABS.map((tab) => (
                            <button
                                key={tab.key}
                                className={`pp-tab${activeTab === tab.key ? ' is-active' : ''}`}
                                onClick={() => setActiveTab(tab.key)}
                            >
                                {tab.label}
                                {tabData.length > 0 && activeTab === tab.key ? null : null}
                            </button>
                        ))}
                    </div>

                    {/* Tab content */}
                    <div className="pp-tab-content">
                        {!loaded ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
                                <span className="spinner-border" role="status" aria-hidden="true" />
                            </div>
                        ) : (
                            <div className="cf-page">
                                {/* Info banner */}
                                {info ? (
                                    <div className="cf-tab-info">
                                        <svg className="cf-tab-info__icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
                                        </svg>
                                        <span><strong>{info.strong}</strong> {info.text}</span>
                                    </div>
                                ) : null}

                                {/* Empty state */}
                                {tabData.length === 0 && activeTab === 'default' ? (
                                    <div className="cf-empty cf-empty--danger">
                                        <svg className="cf-empty__icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                                        </svg>
                                        <p className="cf-empty__text">No Default Careflow set up.</p>
                                    </div>
                                ) : null}
                                {tabData.length === 0 && activeTab !== 'default' ? (
                                    <div className="cf-empty cf-empty--warning">
                                        <svg className="cf-empty__icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
                                        </svg>
                                        <p className="cf-empty__text">No {TABS.find((t) => t.key === activeTab)?.label || 'Careflow'} being utilized.</p>
                                    </div>
                                ) : null}

                                {/* Careflow cards */}
                                {tabData.map((careflow, cIndex) => (
                                    <CareflowCard
                                        key={careflow.careflow_id || cIndex}
                                        careflow={careflow}
                                        careflowTypeId={activeTypeId}
                                        onEditCareflow={() => openEditCareflow(careflow, cIndex)}
                                        onDeleteCareflow={() => confirmDeleteCareflow(careflow, cIndex)}
                                        onAddWorkflow={() => openAddWorkflow(careflow, cIndex)}
                                        onEditWorkflow={(w, wIdx) => openEditWorkflow(careflow, cIndex, w, wIdx)}
                                        onDeleteWorkflow={(w, wIdx) => confirmDeleteWorkflow(careflow, cIndex, w, wIdx)}
                                        onAddEscalation={(w, wIdx) => openAddEscalation(careflow, cIndex, w, wIdx)}
                                        onEditEscalation={(w, wIdx, esc, eIdx) => openEditEscalation(careflow, cIndex, w, wIdx, esc, eIdx)}
                                        expandedEscalations={expandedEscalations}
                                        onToggleEscalation={toggleEscalation}
                                        campusSettings={campusSettings}
                                        escMax={escMax}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Drawers ─────────────────────────────────────────────────────── */}
            <CareflowDrawer
                open={cfDrawerOpen}
                onClose={() => setCfDrawerOpen(false)}
                onSave={handleSaveCareflow}
                onDelete={cfEditTarget ? () => handleDeleteCareflow(cfEditTarget) : undefined}
                saving={saving}
                editTarget={cfEditTarget}
                careflowTypeId={activeTypeId}
                eventCategories={eventCats}
                eventTypes={eventTypes_}
                areaGroups={areaGroups}
                residentGroups={residentGroups}
                devices={devices}
            />

            <WorkflowDrawer
                open={wfDrawerOpen}
                onClose={() => setWfDrawerOpen(false)}
                onSave={handleSaveWorkflow}
                onDelete={wfEditTarget ? () => handleDeleteWorkflow(wfEditTarget) : undefined}
                saving={saving}
                editTarget={wfEditTarget}
                careflowTypeId={activeTypeId}
            />

            <EscalationDrawer
                open={escDrawerOpen}
                onClose={() => setEscDrawerOpen(false)}
                onSave={handleSaveEscalation}
                onDelete={escEditTarget ? () => handleDeleteEscalation(escEditTarget) : undefined}
                saving={saving}
                editTarget={escEditTarget}
                escMax={escMax}
                userGroups={userGroups}
                users={users}
                emergencyContacts={emergencyContacts}
                campusSettings={campusSettings}
            />

            {/* ── Delete dialog ────────────────────────────────────────────────── */}
            <DeleteDialog
                open={deleteOpen}
                onClose={() => setDeleteOpen(false)}
                onConfirm={deleteInfo.action}
                saving={saving}
                title={deleteInfo.title}
                message={deleteInfo.message}
            />

        </>
    );
};

export default CareflowPage;
